import React, {
  createContext,
  PropsWithChildren,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useLocation } from "react-router-dom";
import { UploadItem } from "@/components/upload/types/upload-item";
import { useUploadStateStore } from "@/stores/upload-state.store";
import useTenant from "@/hooks/useTenant";
import { TenantDocumentMetadata } from "@/models/metadata/metadata";
import useWorkspace from "@/hooks/useWorkspace";
import { useSharePointContext } from "./SharePointContext";
import Workspace from "@/models/workspace/Workspace";
import { logger } from "@/lib/logger";

// Import extracted hooks
import { useUploadHubConnection } from "../hooks/useUploadHubConnection";
import { useFileUpload } from "../hooks/useFileUpload";
import { useFolderUpload } from "../hooks/useFolderUpload";
import { useUploadDialogFlow } from "../hooks/useUploadDialogFlow";
import { useSharePointDownloadHandler } from "../hooks/useSharePointDownloadHandler";

// Import types
import { UploadHubContextProps } from "../types/upload.types";

// eslint-disable-next-line react-refresh/only-export-components
export const UploadContext = createContext<UploadHubContextProps | null>(null);

// Re-export types that other components may need
export type {
  DownloadedFileWithIds,
  DownloadedFolderWithIds,
} from "./SharePointProvider";

const UploadHubProvider: React.FC<PropsWithChildren> = ({ children }) => {
  // Get workspaceId from route params or parse from location
  const params = useParams<{ workspaceId: string }>();
  const { downloadQueue, dequeueDownloadBatch } = useSharePointContext();
  const location = useLocation();

  const { useQuerySingle } = useWorkspace();
  const {
    isUploadInProgress,
    setConflictingFiles,
    setIsConflictDialogOpen,
    setPendingUploadFiles,
    setPendingWorkspaceFileNames,
    setPendingFolderId,
    setIsUploadMetadataDialogOpen,
    setPendingMetadataFiles,
    setPendingMetadataWorkspaceFileNames,
    setPendingMetadataFolderId,
    setPendingFolderMetadataMap,
    setPendingFileMetadataMap,
  } = useUploadStateStore();

  const { useQueryCurrent } = useTenant();
  const tenantQuery = useQueryCurrent(true);

  // Try to get workspaceId from params first, then parse from pathname
  const workspaceId =
    params.workspaceId ||
    (location.pathname.match(/\/workspaces\/([^/]+)/)?.[1] ?? null);

  // Get workspace data for conflict checking
  const workspaceQuery = useQuerySingle(workspaceId);

  // Helper function to ensure workspace data is available
  const ensureWorkspaceData =
    useCallback(async (): Promise<Workspace | null> => {
      if (!workspaceId) {
        logger.warn("No workspace id to load workspace data for an upload", {
          operation: "ensureWorkspaceData",
        });
        return null;
      }

      if (workspaceQuery.data) {
        return workspaceQuery.data;
      }

      if (workspaceQuery.isPending) {
        try {
          const result = await workspaceQuery.refetch();
          return result.data || null;
        } catch (error) {
          logger.warn("Failed to refetch the workspace for an upload", {
            operation: "ensureWorkspaceData",
            error,
            workspaceId,
          });
          return null;
        }
      }

      try {
        const result = await workspaceQuery.refetch();
        return result.data || null;
      } catch (error) {
        logger.warn("Failed to refetch the workspace for an upload", {
          operation: "ensureWorkspaceData",
          error,
          workspaceId,
        });
        return null;
      }
    }, [workspaceId, workspaceQuery]);

  const ensureTenantMetadata = useCallback(async (): Promise<
    TenantDocumentMetadata[]
  > => {
    if (tenantQuery.data?.tenantDocumentMetadataList) {
      return tenantQuery.data.tenantDocumentMetadataList;
    }

    if (tenantQuery.isPending) {
      try {
        const result = await tenantQuery.refetch();
        return result.data?.tenantDocumentMetadataList || [];
      } catch (error) {
        logger.warn("Failed to refetch tenant metadata for an upload", {
          operation: "ensureTenantMetadata",
          error,
        });
        return [];
      }
    }

    try {
      const result = await tenantQuery.refetch();
      return result.data?.tenantDocumentMetadataList || [];
    } catch (error) {
      logger.warn("Failed to refetch tenant metadata for an upload", {
        operation: "ensureTenantMetadata",
        error,
      });
      return [];
    }
  }, [tenantQuery]);

  // Upload state management
  const uploadsByWorkspaceRef = useRef<Map<string, UploadItem[]>>(new Map());
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const currentWorkspaceIdRef = useRef<string | null>(null);

  // Helper function to update uploads for a specific workspace
  const updateUploadsForWorkspace = useCallback(
    (
      targetWorkspaceId: string,
      updater: (prev: UploadItem[]) => UploadItem[]
    ) => {
      const currentUploads =
        uploadsByWorkspaceRef.current.get(targetWorkspaceId) || [];
      const newUploads = updater(currentUploads);

      uploadsByWorkspaceRef.current.set(targetWorkspaceId, newUploads);

      if (currentWorkspaceIdRef.current === targetWorkspaceId) {
        setUploads(newUploads);
      }
    },
    []
  );

  // Load uploads for current workspace when workspaceId changes
  useEffect(() => {
    if (!workspaceId) {
      setUploads([]);
      currentWorkspaceIdRef.current = null;
      return;
    }

    currentWorkspaceIdRef.current = workspaceId;
    const workspaceUploads =
      uploadsByWorkspaceRef.current.get(workspaceId) || [];
    setUploads(workspaceUploads);
  }, [workspaceId]);

  // Wrapper for setUploads that updates the workspace-specific storage
  const setUploadsWrapper = useCallback(
    (updater: React.SetStateAction<UploadItem[]>) => {
      const currentWorkspaceId = currentWorkspaceIdRef.current;
      if (!currentWorkspaceId) return;

      updateUploadsForWorkspace(currentWorkspaceId, (prev) => {
        if (typeof updater === "function") {
          return updater(prev);
        }
        return updater;
      });
    },
    [updateUploadsForWorkspace]
  );

  // Refs for file and folder input
  const fileInputRef = useRef<HTMLInputElement>(
    null
  ) as RefObject<HTMLInputElement>;
  const folderInputRef = useRef<HTMLInputElement>(
    null
  ) as RefObject<HTMLInputElement>;

  // Use extracted hooks
  useUploadHubConnection({
    workspaceId,
    locationPathname: location.pathname,
    uploadsByWorkspaceRef,
    updateUploadsForWorkspace,
  });

  const { handleUploadFiles, onUploadFiles } = useFileUpload({
    workspaceId,
    currentWorkspaceIdRef,
    updateUploadsForWorkspace,
  });

  const { handleFolderUpload } = useFolderUpload({
    workspaceId,
    currentWorkspaceIdRef,
    updateUploadsForWorkspace,
    setConflictingFiles,
    setPendingUploadFiles,
    setPendingWorkspaceFileNames,
    setPendingFolderId,
    setPendingFolderMetadataMap,
    setPendingFileMetadataMap,
    setIsConflictDialogOpen,
    setPendingMetadataFiles,
    setPendingMetadataWorkspaceFileNames,
    setPendingMetadataFolderId,
    setIsUploadMetadataDialogOpen,
    ensureTenantMetadata,
  });

  const {
    onFileInputChange,
    onFolderInputChange,
    onOverwriteFilesCancel,
    onOverwriteFilesConfirm,
    onMetadataCancel,
    onMetadataConfirm,
  } = useUploadDialogFlow({
    workspaceId,
    currentWorkspaceIdRef,
    fileInputRef,
    onUploadFiles,
    handleUploadFiles,
    handleFolderUpload,
    ensureTenantMetadata,
    ensureWorkspaceData,
    workspaceData: workspaceQuery.data,
  });

  const { createFileListFromDownloadedFiles } = useSharePointDownloadHandler({
    workspaceId,
    downloadQueue,
    dequeueDownloadBatch,
    workspaceData: workspaceQuery.data,
    isUploadInProgress,
    handleUploadFiles,
    handleFolderUpload,
    ensureTenantMetadata,
    ensureWorkspaceData,
  });

  // Click handlers for file inputs
  const onUploadFilesFromComputer = useCallback(() => {
    if (fileInputRef.current?.value !== "") {
      fileInputRef.current.value = "";
    }
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [fileInputRef]);

  const onUploadFolderFromComputer = useCallback(() => {
    if (folderInputRef.current) {
      if (folderInputRef.current.value !== "") {
        folderInputRef.current.value = "";
      }
      folderInputRef.current.click();
    }
  }, [folderInputRef]);

  // Context value
  const contextValue = useMemo(
    () => ({
      uploads,
      fileInputRef,
      folderInputRef,
      setUploads: setUploadsWrapper,
      onFileInputChange,
      onFolderInputChange,
      onUploadFilesFromComputer,
      onUploadFolderFromComputer,
      onMetadataCancel,
      onMetadataConfirm,
      onOverwriteFilesCancel,
      onOverwriteFilesConfirm,
      handleFolderUpload,
      createFileListFromDownloadedFiles,
    }),
    [
      uploads,
      setUploadsWrapper,
      onFileInputChange,
      onFolderInputChange,
      onUploadFilesFromComputer,
      onUploadFolderFromComputer,
      onMetadataCancel,
      onMetadataConfirm,
      onOverwriteFilesCancel,
      onOverwriteFilesConfirm,
      handleFolderUpload,
      createFileListFromDownloadedFiles,
    ]
  );

  return (
    <UploadContext.Provider value={contextValue}>
      {children}
    </UploadContext.Provider>
  );
};

export default UploadHubProvider;
